import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface PhoneAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Format a US 10-digit number as (XXX) XXX-XXXX while typing.
 * Strips all non-digits and caps at 10.
 */
const formatUS = (raw: string) => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const PhoneAuthDialog = ({ open, onOpenChange }: PhoneAuthDialogProps) => {
  const [intl, setIntl] = useState(false);
  // US mode: 10 digits formatted; intl mode: country code + national number
  const [usPhone, setUsPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [intlNumber, setIntlNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  /** Build the canonical E.164 string for the current mode. */
  const e164 = useMemo(() => {
    if (intl) {
      const cc = countryCode.trim().replace(/[^\d+]/g, "");
      const n = intlNumber.replace(/\D/g, "");
      const ccNorm = cc.startsWith("+") ? cc : `+${cc}`;
      return `${ccNorm}${n}`;
    }
    const digits = usPhone.replace(/\D/g, "");
    return digits ? `+1${digits}` : "";
  }, [intl, countryCode, intlNumber, usPhone]);

  const reset = () => {
    setIntl(false);
    setUsPhone("");
    setCountryCode("+1");
    setIntlNumber("");
    setOtpCode("");
    setOtpSent(false);
    setBusy(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\+[1-9]\d{6,14}$/.test(e164)) {
      toast.error(
        intl
          ? "Enter a valid country code and phone number."
          : "Enter a valid 10-digit US phone number."
      );
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({ phone: e164 });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setOtpSent(true);
    toast.success("Code sent. Check your messages.");
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { error } = await supabase.auth.verifyOtp({
      phone: e164,
      token: otpCode.trim(),
      type: "sms",
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    handleClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Continue with phone</DialogTitle>
          <DialogDescription>
            {otpSent
              ? `We sent a 6-digit code to ${e164}.`
              : "We'll text you a 6-digit verification code."}
          </DialogDescription>
        </DialogHeader>

        {!otpSent ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            {!intl ? (
              <div>
                <Label htmlFor="phone-us">Phone number</Label>
                <Input
                  id="phone-us"
                  type="tel"
                  required
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="(202) 555-0123"
                  value={usPhone}
                  onChange={(e) => setUsPhone(formatUS(e.target.value))}
                  autoFocus
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  US numbers only. Outside the US?{" "}
                  <button
                    type="button"
                    onClick={() => setIntl(true)}
                    className="underline hover:text-foreground"
                  >
                    Use country code instead
                  </button>
                  .
                </p>
              </div>
            ) : (
              <div>
                <Label>Phone number</Label>
                <div className="flex gap-2">
                  <Input
                    aria-label="Country code"
                    type="tel"
                    required
                    inputMode="numeric"
                    placeholder="+44"
                    value={countryCode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d+]/g, "").slice(0, 5);
                      setCountryCode(v.startsWith("+") ? v : `+${v.replace(/\+/g, "")}`);
                    }}
                    className="w-24"
                  />
                  <Input
                    aria-label="Phone number"
                    type="tel"
                    required
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="2025550123"
                    value={intlNumber}
                    onChange={(e) =>
                      setIntlNumber(e.target.value.replace(/\D/g, "").slice(0, 14))
                    }
                    className="flex-1"
                    autoFocus
                  />
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Enter your country code and national number.{" "}
                  <button
                    type="button"
                    onClick={() => setIntl(false)}
                    className="underline hover:text-foreground"
                  >
                    Back to US
                  </button>
                  .
                </p>
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Sending…" : "Send code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div>
              <Label htmlFor="otp-dialog">Verification code</Label>
              <Input
                id="otp-dialog"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                placeholder="123456"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoFocus
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Code expires in a few minutes.
              </p>
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Verifying…" : "Verify & continue"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setOtpCode("");
              }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center"
            >
              Use a different number
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneAuthDialog;
